import { Component, OnInit, OnDestroy, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { LogError, LogStatus, Metadata, SetProductionStatus } from '@memberjunction/core';
import { setupGraphQLClient, GraphQLProviderConfigData } from '@memberjunction/graphql-dataprovider';
import { lastValueFrom, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { MJAuthBase } from '@memberjunction/ng-auth-services';
import { LoadGeneratedEntities } from 'mj_generatedentities'
import { AUTH_RETRY_KEY, JWT_RETRY_KEY, RETRY_WINDOW_HOURS } from './utils/constants';
import { UserService } from './services/user.service';
import { SharedData } from './utils/shared-data';
import { SessionManagementService, SessionStrategy } from './services/session-management.service';
import { ImpersonationService } from './services/impersonation.service';
import { UserAuthService } from './services/user-auth.service';
import { PortalAccessLogService } from './services/portal-access-log.service';
LoadGeneratedEntities(); // forces the generated entities library to load up, sometimes tree shaking in the build process can break this, so this is a workaround that ensures it always happens

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  public title = 'Chapter Portal';
  public initalPath = '/';
  public HasError = false;
  public ErrorMessage: any = '';
  public readonly environment = environment;

  // Navigation state
  public isNavCollapsed = false;
  public notificationCount = 0;

  // Authentication state
  public isAuthLoading = true;
  
  // Navigation loading state
  public isNavigationReady = false;

  // Component cleanup
  private readonly destroy$ = new Subject<void>();

  // Store OAuth detection from constructor - URL may change before subscription fires
  private readonly hasOAuthParamsOnInit: boolean;

  // Prevent concurrent handleLogin calls during OAuth callback
  private isHandlingLogin = false;

  constructor(
    private router: Router,
    public authBase: MJAuthBase,
    private userService: UserService,
    private sharedData: SharedData,
    private sessionManagementService: SessionManagementService,
    public impersonationService: ImpersonationService,
    private userAuthService: UserAuthService,
    private portalAccessLogService: PortalAccessLogService
  ) {
    // Detect OAuth params ONCE at construction time, before Okta provider processes them
    this.hasOAuthParamsOnInit = window.location.search.includes('code=') ||
                                window.location.search.includes('state=') ||
                                window.location.hash.includes('id_token=');
  }

  /**
   * Handles the login process and sets up the GraphQL client
   * @param token - The authentication token
   * @param claims - The user claims
   */
  async handleLogin(token: string, claims: any): Promise<void> {

    // Prevent concurrent handleLogin calls (getUserClaims can fire multiple times)
    if (this.isHandlingLogin) return;
    if (!token) return;

    this.isHandlingLogin = true;
    const { GRAPHQL_URI: url, GRAPHQL_WS_URI: wsurl, MJ_CORE_SCHEMA_NAME } = environment;

    try {
      LogStatus('Setting up GraphQL client...');
      const start = Date.now();
      const config = new GraphQLProviderConfigData(
        token,
        url,
        wsurl,
        this.getTokenRefreshCallback(),
        MJ_CORE_SCHEMA_NAME
      );

      await setupGraphQLClient(config);
      LogStatus(`GraphQL Client Setup took ${Date.now() - start}ms`);

      await this.validateUserAccess();
      await this.setupUserData();

      // Extract auth info from claims
      const personId = claims?.personId;
      const auraUniqueId = claims?.auraUniqueId;
      const role = claims?.userRole;

      if (!personId || !auraUniqueId) {
        LogError('❌ Missing personId or auraUniqueId from claims');
        throw new Error('Missing required authentication information from Okta claims');
      }

      LogStatus(`App: Extracted auth info - personId: ${personId}, auraUniqueId: ${auraUniqueId}, role: ${role || 'not provided'}`);

      // ⭐ CHECK FOR ACTIVE IMPERSONATION SESSION FIRST
      const activeImpersonationSession = this.impersonationService.getCurrentSession();

      if (activeImpersonationSession) {
        // User is impersonating - do NOT update role with real user's role
        LogStatus(`⚠️ Active impersonation session detected - skipping role update for real user`);
        LogStatus(`   Real user: ${activeImpersonationSession.originalUser.Name}`);
        LogStatus(`   Impersonated user: ${activeImpersonationSession.impersonatedUser.Name}`);

        // Still store personId/auraUniqueId for the real user (needed for API calls)
        // but DON'T fetch/update role - keep the impersonated user's role active
        this.userAuthService.updateAuthInfoOnly(personId, auraUniqueId);

        // Fetch impersonated user's role to ensure it's current
        const impersonatedUserId = activeImpersonationSession.impersonatedUser.ID;
        const impersonatedUserEmail = activeImpersonationSession.impersonatedUser.Email;

        try {
          const impersonatedRole = await this.userAuthService.getRoleByUserId(impersonatedUserId, impersonatedUserEmail);
          if (impersonatedRole) {
            LogStatus(`✅ Using impersonated user's role: ${impersonatedRole}`);
          } else {
            LogError(`❌ Failed to get role for impersonated user ${impersonatedUserEmail}`);
            throw new Error('Failed to retrieve impersonated user role during token refresh');
          }
        } catch (error) {
          LogError('❌ Error fetching impersonated user role:', undefined, error);
          throw new Error('Failed to maintain impersonation session during login');
        }
      } else {
        // Normal login flow - no impersonation active
        LogStatus('No active impersonation session - proceeding with normal role fetch');

        // Store auth info and ensure ExtendedUsers record (this fetches role from API)
        await this.userAuthService.setUserAuthInfo(personId, auraUniqueId, role);

        // Validate that role was successfully fetched, if not, fetch it now (defensive)
        if (!this.userAuthService.getUserRole()) {
          LogStatus('⚠️ Role not cached after login setup, fetching from API...');

          try {
            const roleFetchStart = Date.now();
            await this.userAuthService.fetchUserRole(personId, auraUniqueId);
            LogStatus(`✅ User role fetched from API in ${Date.now() - roleFetchStart}ms: ${this.userAuthService.getUserRole()}`);
          } catch (error) {
            LogError('❌ Failed to fetch user role from API:', undefined, error);
            throw new Error('Failed to retrieve user role during login');
          }
        }

        LogStatus(`✅ User role ready before navigation: ${this.userAuthService.getUserRole()}`);
      }

      // Log successful login (non-blocking - runs in background)
      const md = new Metadata();
      this.portalAccessLogService.logLogin(
        md.CurrentUser!,
        claims,
        this.impersonationService.getCurrentSession()?.id
      ).catch(error => {
        LogError('Failed to log login event:', undefined, error);
      });

      localStorage.removeItem(JWT_RETRY_KEY);
    } catch (err) {
      await this.handleLoginError(err);
    } finally {
      // Reset flag so subsequent login attempts can proceed
      this.isHandlingLogin = false;
    }
  }

  /**
   * Gets the callback function for token refresh
   */
  private getTokenRefreshCallback(): () => Promise<string> {
    return async () => {
      const refresh$ = await this.authBase.refresh();
      const claims = await lastValueFrom(refresh$);
      return environment.AUTH_TYPE === 'auth0' ? claims?.__raw : claims?.idToken;
    };
  }

  /**
   * Validates if the user has access to the application
   */
  private async validateUserAccess(): Promise<void> {
    const md = new Metadata();
    if (!md.CurrentUser) {
      this.HasError = true;
      this.ErrorMessage = 'You dont have access to the application, contact your system administrator.';
      throw new Error(this.ErrorMessage);
    }
    this.userService.setUserInfo(md.CurrentUser);
  }

  /**
   * Sets up user data and refreshes shared data
   */
  private async setupUserData(): Promise<void> {
    const setupStart = Date.now();
    await this.sharedData.Refresh();
    const setupElapsed = (Date.now() - setupStart) / 1000;
    LogStatus(`setupUserData() took ${setupElapsed} seconds`);
    
    // Load initial navigation state from localStorage
    const savedNavState = localStorage.getItem('navCollapsed');
    if (savedNavState) {
      this.isNavCollapsed = savedNavState === 'true';
      // Set initial body class
      const body = document.body;
      if (this.isNavCollapsed) {
        body.classList.add('nav-collapsed');
      } else {
        body.classList.remove('nav-collapsed');
      }
    }
  }

  /**
   * Handles login errors and retries if necessary
   */
  private async handleLoginError(err: any): Promise<void> {
    const lastRetryDateTime = localStorage.getItem(AUTH_RETRY_KEY);
    const yesterday = Date.now() - RETRY_WINDOW_HOURS * 60 * 60 * 1000;
    const retriedRecently = lastRetryDateTime && +new Date(lastRetryDateTime) > yesterday;
    const expiryError = this.authBase.checkExpiredTokenError(err?.response?.errors?.[0]?.message);

    if (!retriedRecently && expiryError) {
      LogStatus('JWT Expired, retrying once: ' + err);
      localStorage.setItem(AUTH_RETRY_KEY, new Date().toISOString());
      const login$ = await this.authBase.login({ appState: { target: window.location.pathname } });
      await lastValueFrom(login$);
    } else {
      this.HasError = true;
      this.ErrorMessage = err;
      LogError('Error Logging In: ' + err);
      throw err;
    }
  }

  async setupAuth() {
    try {
      // Store initial path before any redirects
      this.initalPath = window.location.pathname + (window.location.search ? window.location.search : '');
      
      // Check if already authenticated first
      const isAuthenticated = await this.authBase.isAuthenticated().pipe(take(1)).toPromise();
      
      if (isAuthenticated) {
        this.authBase.authenticated = true;
        
        // Initialize user data if authenticated
        const claims = await this.authBase.getUserClaims().then(claimsObs => claimsObs.pipe(take(1)).toPromise());
        if (claims) {
          const token = environment.AUTH_TYPE === 'auth0' ? claims?.__raw : claims?.idToken;
          const result = claims.idTokenClaims 
            ? { ...claims, ...claims.idTokenClaims }
            : claims;
          await this.handleLogin(token, result);
        }
        
        // Navigate to the intended route after successful authentication
        await this.navigateAfterAuth();
        this.isAuthLoading = false;
        return;
      }

      // If not authenticated, set up the authentication flow      
      this.authBase.getUserClaims().then(claims => {
        claims.subscribe({
          next: async (claims: any) => {
            if (claims) {
              const token = environment.AUTH_TYPE === 'auth0' ? claims?.__raw : claims?.idToken;
              const result = claims.idTokenClaims 
                ? { ...claims, ...claims.idTokenClaims }
                : claims;

              try {
                await this.handleLogin(token, result);
                this.authBase.authenticated = true;
                await this.navigateAfterAuth();
                this.isAuthLoading = false;
              } catch (error) {
                LogError('Error during login process:', undefined, error);
                this.authBase.authenticated = false;

                // Don't show login page if we're in OAuth callback - keep loading spinner
                // getUserClaims may fire again with complete data
                if (!this.hasOAuthParamsOnInit) {
                  this.isAuthLoading = false;
                }
              }
            } else {
              // Don't show login page if we're in the middle of OAuth callback
              // Use stored OAuth detection from constructor - URL may have changed by now
              if (!this.hasOAuthParamsOnInit) {
                // No OAuth callback happening, safe to show login page
                this.isAuthLoading = false;
              } else {
                // OAuth callback in progress - set safety timeout
                // If claims don't arrive in 10 seconds, something went wrong
                LogStatus('OAuth callback detected, waiting for claims...');
                setTimeout(() => {
                  if (this.isAuthLoading && !this.authBase.authenticated) {
                    LogError('OAuth callback timeout - showing login page');
                    this.isAuthLoading = false;
                  }
                }, 10000);
              }
            }
          },
          error: (err: any) => {
            LogError('Error Logging In: ' + err);
            this.authBase.authenticated = false;
            this.isAuthLoading = false;
          }
        });
      }).catch(err => {
        LogError('Error getting user claims:', undefined, err);
        this.authBase.authenticated = false;
        this.isAuthLoading = false;
      });
    } catch (error) {
      LogError('Auth setup error:', undefined, error);
      this.authBase.authenticated = false;
      this.isAuthLoading = false;
    }
  }

  /**
   * Navigate to the appropriate route after authentication
   */
  private async navigateAfterAuth(): Promise<void> {
    try {
      // If we have a stored initial path and it's not root, navigate there
      if (this.initalPath && this.initalPath !== '/' && this.initalPath !== '') {
        await this.router.navigateByUrl(this.initalPath);
      } else {
        await this.router.navigate(['/dashboard']);
      }
    } catch (error) {
      // Fallback to dashboard
      await this.router.navigate(['/dashboard']);
    }
  }

  ngOnInit() {
    SetProductionStatus(environment.production)
    this.setupAuth();
    this.configureSessionManagement();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Configure session management with environment settings
   */
  private configureSessionManagement(): void {
    if (environment.SESSION_CONFIG) {
      const sessionConfig = {
        ...environment.SESSION_CONFIG,
        strategy: environment.SESSION_CONFIG.strategy as SessionStrategy
      };
      this.sessionManagementService.configureSession(sessionConfig);
    }
  }

  
  /**
   * Handle navigation collapse toggle
   */
  onNavCollapseToggle(collapsed: boolean): void {
    this.isNavCollapsed = collapsed;
    // Save state to localStorage
    localStorage.setItem('navCollapsed', collapsed.toString());
    
    // Update body classes for layout coordination
    const body = document.body;
    if (collapsed) {
      body.classList.add('nav-collapsed');
    } else {
      body.classList.remove('nav-collapsed');
    }
  }

  /**
   * Handle navigation ready event
   */
  onNavigationReady(ready: boolean): void {
    this.isNavigationReady = ready;
  }

  /**
   * Get appropriate loading message based on current loading state
   */
  getLoadingMessage(): string {
    if (this.isAuthLoading) {
      return 'Loading application...';
    } else if (!this.isNavigationReady && this.authBase.authenticated) {
      return 'Loading navigation...';
    }
    return 'Loading...';
  }

  /**
   * Handle user menu actions
   */
  async onUserMenuAction(action: string): Promise<void> {
    switch(action) {
      case 'profile':
        this.router.navigate(['/profile']);
        break;
      
      case 'settings':
        this.router.navigate(['/settings']);
        break;
      
      case 'logout':
        // Calculate session duration before logout
        const sessionDuration = this.portalAccessLogService.getSessionDuration();

        // Log logout event (non-blocking - runs in background)
        const currentUser = this.userService.getUserInfo();
        if (currentUser) {
          this.portalAccessLogService.logLogout(
            currentUser,
            sessionDuration,
            this.impersonationService.getCurrentSession()?.id
          ).catch(error => {
            LogError('Failed to log logout event:', undefined, error);
          });
        }

        // Clear impersonation session before logout
        await this.impersonationService.endCurrentSession('manual');
        await this.authBase.logout();
        break;
      
      default:
        LogStatus('Unknown user menu action:', action);
    }
  }
}